import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Badge,
  Button,
  Card,
  ConfirmModal,
  FILTER_SELECT_CLASS,
  Modal,
  useModalRequestClose,
} from '@safetyhub/ui';
import { objectParticleOf } from '@safetyhub/shared';
import { adminApi, extractApiError } from '../lib/api';
import type { IndustryWorkCategoryNode } from '../lib/api';
import { useToastStore } from '../stores/toast';

/** 업종 셀렉트의 「공통 코어」 항목 값 — 서버에는 industryId를 안 보내는 축이다. */
const COMMON_AXIS = '__common__';

type FormMode =
  | { kind: 'create-parent' }
  | { kind: 'create-child'; parent: IndustryWorkCategoryNode }
  | { kind: 'edit'; target: IndustryWorkCategoryNode };

interface FormValues {
  name: string;
  order: number;
}

function toFormValues(mode: FormMode): FormValues {
  return mode.kind === 'edit'
    ? { name: mode.target.name, order: mode.target.order }
    : { name: '', order: 0 };
}

/**
 * 정렬 순서 입력 정규화 — 0 이상 정수로 자른다.
 *
 * `min={0}`은 브라우저 힌트일 뿐 **값을 막지 못한다**(스피너만 멈추고, 타이핑·붙여넣기로 들어온
 * 음수는 그대로 제출된다). 소수·공백·`e` 같은 number 입력 특유의 중간 상태도 여기서 흡수한다.
 */
function normalizeOrder(raw: string): number {
  const n = Number(raw);
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.floor(n));
}

/**
 * admin 「업종별 공종 분류 관리」 — `industry_work_categories` 운영 화면.
 *
 * SIF 참조 사전(`/work-classifications`)과 **다른 표**다. 이쪽은 우리가 정의·개정하는 운영
 * 어휘이고, 사진 분석 1차가 여기서 목록 선택으로 분류값을 확정한다(결정 기록
 * `2026-09-02-tbm-work-type-axis-redesign.md` ⑥).
 *
 * 축이 둘이다: **공통 코어**(industry_id NULL — 모든 업종 폴백)와 **업종별**(KSIC). 분석이
 * 실제로 보는 후보는 둘의 합집합이지만, 이 화면의 기본값은 **고른 축만** 보여준다 — 공통
 * 코어를 업종 화면에서 무심코 고치면 전 업종에 번지기 때문이다. 합집합을 보고 싶으면
 * 「공통 코어 함께 보기」를 켠다(공통 행은 「공통」 배지 + 편집 잠금).
 *
 * 삭제 버튼이 없는 것은 의도다 — 고른 분류값은 발견사항·보고서에 문자열로 박제되므로
 * 은퇴는 비활성화로만 한다.
 */
export default function IndustryWorkCategories() {
  const queryClient = useQueryClient();
  const showToast = useToastStore((s) => s.showToast);

  const [axis, setAxis] = useState<string>(COMMON_AXIS);
  const [includeCommon, setIncludeCommon] = useState(false);
  const [search, setSearch] = useState('');
  const [formMode, setFormMode] = useState<FormMode | null>(null);
  const [deactivateTarget, setDeactivateTarget] =
    useState<IndustryWorkCategoryNode | null>(null);

  const industryId = axis === COMMON_AXIS ? undefined : axis;

  const { data: industriesData } = useQuery({
    queryKey: ['admin', 'industries', 'level1'],
    queryFn: () => adminApi.getIndustries({ level: 1, isActive: true }),
  });
  const industries = useMemo(
    () => industriesData?.data ?? [],
    [industriesData]
  );

  /**
   * 검색어는 **디바운스한 값**으로만 조회한다 — queryKey에 직결하면 타건마다 새 키가 되어
   * 요청이 글자 수만큼 나가고, 그때마다 캐시 미스라 목록이 통째로 비었다가 다시 그려진다.
   */
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  const listQueryKey = [
    'admin',
    'industry-work-categories',
    industryId ?? COMMON_AXIS,
    includeCommon,
    debouncedSearch,
  ] as const;

  const { data: listData, isLoading } = useQuery({
    queryKey: listQueryKey,
    queryFn: () =>
      adminApi.getIndustryWorkCategories({
        industryId,
        // 공통 코어 축을 보고 있을 때 합집합 옵션은 의미가 없다(자기 자신이다).
        includeCommon: industryId ? includeCommon : false,
        search: debouncedSearch.trim() || undefined,
      }),
    // 디바운스가 요청 수를 줄여도 **키가 바뀌는 순간의 빈 목록**은 그대로 남는다.
    // 이전 결과를 자리 지킴으로 두면 새 응답이 올 때까지 목록이 사라지지 않는다.
    placeholderData: (prev) => prev,
  });
  const nodes = listData?.data ?? [];

  const invalidateList = () =>
    queryClient.invalidateQueries({
      queryKey: ['admin', 'industry-work-categories'],
    });

  const createMutation = useMutation({
    mutationFn: (input: {
      industryId?: string | null;
      parentId?: string | null;
      name: string;
      order: number;
    }) => adminApi.createIndustryWorkCategory(input),
    onSuccess: () => {
      showToast('분류를 추가했습니다.', 'success');
      setFormMode(null);
      void invalidateList();
    },
    onError: (error: unknown) =>
      showToast(extractApiError(error).message, 'error'),
  });

  const updateMutation = useMutation({
    mutationFn: (input: {
      id: string;
      name?: string;
      order?: number;
      isActive?: boolean;
    }) => {
      const { id, ...rest } = input;
      return adminApi.updateIndustryWorkCategory(id, rest);
    },
    onSuccess: (_result, input) => {
      showToast(
        input.isActive === false
          ? '분류를 비활성화했습니다.'
          : input.isActive === true
            ? '분류를 활성화했습니다.'
            : '분류를 수정했습니다.',
        'success'
      );
      setFormMode(null);
      setDeactivateTarget(null);
      void invalidateList();
    },
    onError: (error: unknown) =>
      showToast(extractApiError(error).message, 'error'),
  });

  const busy = createMutation.isPending || updateMutation.isPending;

  const handleSubmit = (values: FormValues) => {
    if (!formMode) return;
    if (formMode.kind === 'edit') {
      updateMutation.mutate({
        id: formMode.target.id,
        name: values.name.trim(),
        order: values.order,
      });
      return;
    }
    createMutation.mutate({
      // 중분류의 축은 서버가 부모에서 가져간다 — 여기서는 대분류일 때만 축을 싣는다.
      industryId:
        formMode.kind === 'create-parent' ? (industryId ?? null) : undefined,
      parentId: formMode.kind === 'create-child' ? formMode.parent.id : null,
      name: values.name.trim(),
      order: values.order,
    });
  };

  const toggleActive = (node: IndustryWorkCategoryNode) => {
    if (node.isActive) {
      setDeactivateTarget(node);
      return;
    }
    updateMutation.mutate({ id: node.id, isActive: true });
  };

  const axisLabel =
    axis === COMMON_AXIS
      ? '공통 코어'
      : (industries.find((i) => i.id === axis)?.name ?? '업종');

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900">
          업종별 공종 분류 관리
        </h2>
        <p className="mt-1 text-sm text-gray-500">
          사진 분석이 작업 분류를 고를 때 쓰는 운영 어휘입니다. 공통 코어는 모든
          업종의 폴백이고, 업종별 항목은 그 업종에서만 후보로 나옵니다.
        </p>
      </div>

      {/*
        비활성화의 파급을 운영자가 알아야 한다 — 목록만 보면 "체크 하나 끄는 것"으로
        보이지만 실제로는 그 계보가 통째로 분석에서 빠진다(후보 산출이 활성 대분류만 읽는다).
      */}
      <div className="mb-4 rounded-card border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
        <strong className="font-semibold">비활성화는 삭제가 아닙니다.</strong>{' '}
        비활성으로 바꾼 항목은 다음 분석부터 후보에 나오지 않고,{' '}
        <strong className="font-semibold">
          공종(대분류)을 끄면 그 아래 작업명들도 함께 쓰이지 않습니다
        </strong>
        . 이미 만들어진 발견사항·보고서에 들어간 값은 그대로 남으며, 다시 활성화하면
        원래대로 돌아옵니다.
      </div>

      {/* 도구 바 */}
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <select
          className={FILTER_SELECT_CLASS}
          value={axis}
          onChange={(e) => setAxis(e.target.value)}
          aria-label="업종 선택"
        >
          <option value={COMMON_AXIS}>공통 코어 (모든 업종)</option>
          {industries.map((industry) => (
            <option key={industry.id} value={industry.id}>
              {industry.name}
            </option>
          ))}
        </select>

        {industryId && (
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-gray-300"
              checked={includeCommon}
              onChange={(e) => setIncludeCommon(e.target.checked)}
            />
            공통 코어 함께 보기
          </label>
        )}

        <input
          type="search"
          className={`${FILTER_SELECT_CLASS} min-w-[220px] font-normal`}
          // placeholder는 이름이 아니다 — 값이 차면 사라지고, 보조기술은 칸의 정체를 잃는다.
          aria-label="공종·작업명 검색"
          placeholder="공종·작업명 검색"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        <div className="ml-auto">
          <Button
            variant="primary"
            size="md"
            onClick={() => setFormMode({ kind: 'create-parent' })}
          >
            공종(대분류) 추가
          </Button>
        </div>
      </div>

      {isLoading ? (
        <Card radius="lg">
          <div className="py-10 text-center text-sm text-gray-500">
            불러오는 중…
          </div>
        </Card>
      ) : nodes.length === 0 ? (
        <Card radius="lg">
          <div className="py-10 text-center text-sm text-gray-500">
            {/* 화면에 그려진 목록을 만든 질의로 판단한다 — 입력 중인 `search`로 고르면
                디바운스 구간 동안 "검색 결과가 없습니다"와 전체 목록 문구가 엇갈린다. */}
            {debouncedSearch.trim()
              ? '검색 결과가 없습니다.'
              : `${axisLabel}에 등록된 공종이 없습니다.`}
          </div>
        </Card>
      ) : (
        <div className="space-y-3">
          {nodes.map((parent) => (
            <CategoryGroup
              key={parent.id}
              parent={parent}
              onIndustryAxis={!!industryId}
              busy={busy}
              onAddChild={() => setFormMode({ kind: 'create-child', parent })}
              onEdit={(target) => setFormMode({ kind: 'edit', target })}
              onToggleActive={toggleActive}
            />
          ))}
        </div>
      )}

      {formMode && (
        <CategoryFormModal
          // 대상이 바뀌면 새로 마운트시켜 입력 초기값(dirty 기준선)을 다시 잡는다.
          key={
            formMode.kind === 'edit'
              ? `edit-${formMode.target.id}`
              : formMode.kind === 'create-child'
                ? `child-${formMode.parent.id}`
                : 'create-parent'
          }
          mode={formMode}
          axisLabel={axisLabel}
          busy={busy}
          onSubmit={handleSubmit}
          onClose={() => setFormMode(null)}
        />
      )}

      <ConfirmModal
        open={!!deactivateTarget}
        title="분류 비활성화"
        danger
        confirmLabel="비활성화"
        busy={updateMutation.isPending}
        body={
          deactivateTarget && (
            <div className="space-y-2 text-sm text-gray-700">
              <p>
                「{deactivateTarget.name}」{objectParticleOf(deactivateTarget.name)} 비활성화하면 이후 분석에서 이
                분류를 더 이상 고르지 않습니다.
              </p>
              {deactivateTarget.parentId === null &&
                (deactivateTarget.children?.length ?? 0) > 0 && (
                  <p className="font-medium text-amber-700">
                    이 공종에 딸린 작업명 {deactivateTarget.children?.length}개도
                    함께 쓰이지 않게 됩니다. 목록에는 활성으로 남지만, 상위가
                    꺼진 동안에는 분석에 나오지 않습니다.
                  </p>
                )}
              <p className="text-gray-500">
                이미 만들어진 발견사항·보고서에 들어간 값은 그대로 남습니다.
                필요하면 언제든 다시 활성화할 수 있습니다.
              </p>
            </div>
          )
        }
        onConfirm={() =>
          deactivateTarget &&
          updateMutation.mutate({ id: deactivateTarget.id, isActive: false })
        }
        onClose={() => setDeactivateTarget(null)}
      />
    </div>
  );
}

// ============================================
// 계층 한 덩어리 (공종 + 그 작업명들)
// ============================================

function CategoryGroup({
  parent,
  onIndustryAxis,
  busy,
  onAddChild,
  onEdit,
  onToggleActive,
}: {
  parent: IndustryWorkCategoryNode;
  /** 업종 축을 보고 있는가 — 그때만 공통 코어 행이 읽기 전용이 된다. */
  onIndustryAxis: boolean;
  busy: boolean;
  onAddChild: () => void;
  onEdit: (node: IndustryWorkCategoryNode) => void;
  onToggleActive: (node: IndustryWorkCategoryNode) => void;
}) {
  const children = parent.children ?? [];
  // 공통 코어 축을 직접 보고 있을 때는 전부 편집 대상이다 — 잠그는 것은 **업종 축에 얹혀
  // 보이는** 공통 행뿐(거기서 고치면 다른 업종까지 번진다).
  const parentLocked = onIndustryAxis && parent.isCommon;

  return (
    <Card radius="lg">
      <div className="flex flex-wrap items-center gap-2">
        <span
          className={`text-base font-bold ${
            parent.isActive ? 'text-gray-900' : 'text-gray-400 line-through'
          }`}
        >
          {parent.name}
        </span>
        <SourceBadge node={parent} locked={parentLocked} />
        {!parent.isActive && (
          <Badge tone="slate" size="sm">
            비활성
          </Badge>
        )}
        <span className="text-xs text-gray-400">작업명 {children.length}</span>

        <div className="ml-auto flex items-center gap-2">
          {!parentLocked && (
            <>
              <Button
                variant="ghost"
                size="sm"
                disabled={busy}
                onClick={() => onEdit(parent)}
              >
                수정
              </Button>
              <Button
                variant={parent.isActive ? 'secondary' : 'success'}
                size="sm"
                disabled={busy}
                onClick={() => onToggleActive(parent)}
              >
                {parent.isActive ? '비활성화' : '활성화'}
              </Button>
              <Button
                variant="secondary"
                size="sm"
                disabled={busy}
                onClick={onAddChild}
              >
                작업명 추가
              </Button>
            </>
          )}
        </div>
      </div>

      {children.length > 0 && (
        <ul className="mt-3 divide-y divide-gray-100 border-t border-gray-100">
          {children.map((child) => {
            const childLocked = onIndustryAxis && child.isCommon;
            return (
            <li
              key={child.id}
              className="flex flex-wrap items-center gap-2 py-2"
            >
              <span
                className={`text-sm ${
                  child.isActive
                    ? 'text-gray-800'
                    : 'text-gray-400 line-through'
                }`}
              >
                {child.name}
              </span>
              <SourceBadge node={child} locked={childLocked} />
              {!child.isActive && (
                <Badge tone="slate" size="sm">
                  비활성
                </Badge>
              )}
              {child.isActive && !parent.isActive && (
                // 상위가 꺼지면 후보 산출에서 이 계보가 통째로 빠진다 — 자식 상태만 보고
                // "살아 있다"고 읽지 않도록 화면이 말해 준다(서버는 전파하지 않는다).
                <Badge tone="amber" size="sm">
                  상위 비활성
                </Badge>
              )}

              {!childLocked && (
                <div className="ml-auto flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="xs"
                    disabled={busy}
                    onClick={() => onEdit(child)}
                  >
                    수정
                  </Button>
                  <Button
                    variant={child.isActive ? 'secondary' : 'success'}
                    size="xs"
                    disabled={busy}
                    onClick={() => onToggleActive(child)}
                  >
                    {child.isActive ? '비활성화' : '활성화'}
                  </Button>
                </div>
              )}
            </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
}

function SourceBadge({
  node,
  locked,
}: {
  node: IndustryWorkCategoryNode;
  locked: boolean;
}) {
  return (
    <>
      {locked && (
        <Badge tone="blue" size="sm">
          공통
        </Badge>
      )}
      <Badge tone={node.source === 'seed' ? 'slate' : 'violet'} size="sm">
        {node.source === 'seed' ? '초안' : '운영 등록'}
      </Badge>
    </>
  );
}

// ============================================
// 생성/수정 모달
// ============================================

function CategoryFormModal({
  mode,
  axisLabel,
  busy,
  onSubmit,
  onClose,
}: {
  mode: FormMode;
  axisLabel: string;
  busy: boolean;
  onSubmit: (values: FormValues) => void;
  onClose: () => void;
}) {
  // 열림 시점의 값이 dirty 기준선이다. 대상이 바뀌면 호출부가 key로 remount시킨다.
  // ⚠️ lazy 초기화여야 한다 — `useRef(toFormValues(mode))`는 매 렌더 인자를 평가하고 결과만
  // 버린다(첫 렌더 뒤로는 전부 헛일이고, mode가 무거워지면 그대로 비용이 된다).
  const [initial] = useState<FormValues>(() => toFormValues(mode));
  const [values, setValues] = useState<FormValues>(initial);
  // 정렬 순서 입력의 **원문**. 숫자 state만 두면 칸을 비우는 순간 0으로 튕겨(`Number('') || 0`)
  // "지우고 다시 치기"가 막힌다. 화면은 원문, 저장은 정규화값 — blur에서 둘을 다시 맞춘다.
  const [orderText, setOrderText] = useState<string>(() => String(initial.order));
  const nameRef = useRef<HTMLInputElement>(null);

  const isEdit = mode.kind === 'edit';
  const trimmed = values.name.trim();
  // 생성 모드는 dirty 게이트를 두지 않는다(입력 존재만 본다) — 수정 모드만 스냅샷 대조.
  const isDirty = isEdit
    ? trimmed !== initial.name || values.order !== initial.order
    : trimmed.length > 0;
  const canSubmit = trimmed.length > 0 && !busy && (!isEdit || isDirty);

  const title = isEdit
    ? '분류 수정'
    : mode.kind === 'create-child'
      ? '작업명(중분류) 추가'
      : '공종(대분류) 추가';

  const scopeText =
    mode.kind === 'create-child'
      ? `공종 「${mode.parent.name}」 아래`
      : mode.kind === 'create-parent'
        ? `${axisLabel} 축`
        : null;

  return (
    <Modal
      open
      onClose={onClose}
      title={title}
      size="md"
      isDirty={isEdit && isDirty}
      initialFocusRef={nameRef}
      footer={
        <FormFooter
          isEdit={isEdit}
          isDirty={isDirty}
          canSubmit={canSubmit}
          onSubmit={() => onSubmit(values)}
          onClose={onClose}
        />
      }
    >
      <div className="space-y-4">
        {scopeText && (
          <p className="text-sm text-gray-500">{scopeText}에 추가합니다.</p>
        )}

        <label className="block">
          <span className="mb-1 block text-sm font-medium text-gray-700">
            분류명
          </span>
          <input
            ref={nameRef}
            type="text"
            className="w-full rounded-control border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
            value={values.name}
            maxLength={100}
            placeholder={
              mode.kind === 'create-child' ? '예: 중량물 인양 작업' : '예: 운반·하역'
            }
            onChange={(e) =>
              setValues((prev) => ({ ...prev, name: e.target.value }))
            }
          />
          <span className="mt-1 block text-xs text-gray-400">
            대분류는 명사구(운반·하역), 중분류는 「~작업」 접미가 명명 원칙입니다.
          </span>
        </label>

        <label className="block">
          <span className="mb-1 block text-sm font-medium text-gray-700">
            정렬 순서
          </span>
          <input
            type="number"
            min={0}
            step={1}
            className="w-32 rounded-control border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
            value={orderText}
            onChange={(e) => {
              const raw = e.target.value;
              setOrderText(raw);
              setValues((prev) => ({ ...prev, order: normalizeOrder(raw) }));
            }}
            // 정규화 결과를 화면에 되비춘다 — 저장될 값과 보이는 값이 갈라지면 안 된다
            // (빈 칸으로 두고 저장하면 0이고, `-3`을 넣어도 0이다).
            onBlur={() => setOrderText(String(values.order))}
          />
        </label>

        {isEdit && (
          <p className="text-xs text-gray-400">
            업종·계층은 여기서 바꿀 수 없습니다. 축을 옮겨야 하면 새로 만들고 옛
            항목을 비활성화하세요.
          </p>
        )}
      </div>
    </Modal>
  );
}

/**
 * 푸터는 별도 컴포넌트여야 한다 — `useModalRequestClose`는 Modal children **하위**에서만
 * 값을 잡는다(부모 본문에서 부르면 null이라 dirty 확인이 우회된다).
 */
function FormFooter({
  isEdit,
  isDirty,
  canSubmit,
  onSubmit,
  onClose,
}: {
  isEdit: boolean;
  isDirty: boolean;
  canSubmit: boolean;
  onSubmit: () => void;
  onClose: () => void;
}) {
  const requestClose = useModalRequestClose();
  return (
    <div className="flex w-full items-center gap-3">
      {isEdit && (
        <span className="text-xs text-gray-500">
          {isDirty ? '변경사항이 있습니다' : '변경사항 없음'}
        </span>
      )}
      <div className="ml-auto flex gap-2">
        <Button
          variant="secondary"
          size="md"
          onClick={() => (requestClose ?? onClose)()}
        >
          취소
        </Button>
        <Button
          variant="primary"
          size="md"
          disabled={!canSubmit}
          onClick={onSubmit}
        >
          저장
        </Button>
      </div>
    </div>
  );
}
